import { renderTemplate } from "../templateRenderer";
import { CORPORATE_TEMPLATE, CORPORATE_CSS } from "./loader";
import type { InvoiceTemplateContext } from "../types";

const CORPORATE_PAGE_SIZES = {
  A4: { width: "210mm", hPad: "32px", vPad: "28px", font: "13.5px" },
  A3: { width: "297mm", hPad: "42px", vPad: "34px", font: "15px" },
  "80mm": { width: "72mm", hPad: "10px", vPad: "8px", font: "11px" },
} as const;

const BASE_TEMPLATE = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8"/>
  <meta name="viewport" content="width=device-width,initial-scale=1"/>
  <title>{{ title }}</title>
  <style>{{ styles }}</style>
</head>
<body>
  {{ content | safe }}
</body>
</html>`;

function buildCssVariables(ctx: InvoiceTemplateContext): string {
  const { pageSize, isForPrint } = ctx;
  const is80 = pageSize === "80mm";
  const sz = CORPORATE_PAGE_SIZES[pageSize];

  return `
    :root {
      --inv-font:        ${sz.font};
      --inv-width:       ${sz.width};
      --inv-bg:          ${isForPrint ? "#fff" : "#edf2f7"};
      --inv-body-pad:    ${isForPrint ? "0" : is80 ? "8px 4px" : "28px 20px"};
      --inv-radius:      ${isForPrint ? "0" : "6px"};
      --inv-shadow:      ${!isForPrint ? "0 2px 20px rgba(0,0,0,0.09)" : "none"};
      --inv-card-border: ${isForPrint ? "1px solid #e2e8f0" : "none"};
      --inv-accent-h:    ${is80 ? "5px" : "7px"};
      --inv-hdr-pad:     ${is80 ? "12px 10px 10px" : `22px ${sz.hPad} 18px`};
      --inv-meta-pad:    ${is80 ? "8px 10px" : `16px ${sz.hPad}`};
      --inv-body-pad-in: ${is80 ? "8px 10px" : `${sz.vPad} ${sz.hPad}`};
      --inv-th-pad:      ${is80 ? "5px 4px" : "10px 12px"};
      --inv-td-pad:      ${is80 ? "5px 4px" : "10px 12px"};
      --inv-store-size:  ${is80 ? "1.1em" : "1.55em"};
      --inv-store-sub:   ${is80 ? "0.78em" : "0.82em"};
      --inv-inv-lbl:     ${is80 ? "0.65em" : "0.7em"};
      --inv-inv-num:     ${is80 ? "1.05em" : "1.35em"};
      --inv-inv-date:    ${is80 ? "0.75em" : "0.8em"};
      --inv-meta-title:  ${is80 ? "0.6em" : "0.65em"};
      --inv-meta-p:      ${is80 ? "0.82em" : "0.88em"};
      --inv-tbl-size:    ${is80 ? "0.86em" : "0.9em"};
      --inv-th-size:     ${is80 ? "0.7em" : "0.75em"};
      --inv-tbox-w:      ${is80 ? "100%" : "55%"};
      --inv-tbox-mb:     ${is80 ? "10px" : "18px"};
      --inv-trow-pad:    ${is80 ? "4px 8px" : "7px 14px"};
      --inv-trow-size:   ${is80 ? "0.86em" : "0.9em"};
      --inv-grand-size:  ${is80 ? "0.95em" : "1.0em"};
      --inv-pay-mb:      ${is80 ? "10px" : "16px"};
      --inv-pay-title:   ${is80 ? "0.62em" : "0.68em"};
      --inv-pay-pad:     ${is80 ? "3px 0" : "5px 0"};
      --inv-chip-pad:    ${is80 ? "1px 6px" : "2px 9px"};
      --inv-ftr-pad:     ${is80 ? "8px 10px" : `14px ${sz.hPad}`};
      --inv-ftr-size:    ${is80 ? "0.78em" : "0.82em"};
      --inv-page-size:   ${is80 ? "80mm auto" : pageSize};
    }
  `;
}

export function corporateTemplate(ctx: InvoiceTemplateContext): string {
  const { order, config } = ctx;

  const currency = config.currency || "LKR";
  const items = order.items || [];
  const payments = order.payments || [];
  const is80 = ctx.pageSize === "80mm";
  const dateObj = new Date(order.order_date || order.created_at);

  const contentContext = {
    storeName: config.store_name || "POS System",
    address: config.address || "",
    phone: config.phone || "",
    footer: config.receipt_footer || "Thank you for your business!",
    customerName: order.customer_name || "Walk-in Customer",
    customerPhone: order.customer_phone || "",
    customerAddress: order.customer_address || "",
    orderNo: order.order_no || "",
    dateStr: dateObj.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }),
    timeStr: dateObj.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    }),
    sellingType: order.selling_type === "wholesale" ? "Wholesale" : "Retail",
    orderStatus: order.order_status_name || "Completed",
    currency,
    items,
    payments,
    subtotal: order.subtotal,
    grandTotal: order.grand_total,
    paidAmount: order.paid_amount,
    dueAmount: order.due_amount,
    discountAmount: order.discount_amount,
    exchangeAmount: order.exchange_amount,
    otherCharges: order.other_charges,
    showDisc: parseFloat(order.discount_amount) > 0,
    showExch: parseFloat(order.exchange_amount) > 0,
    showChg: parseFloat(order.other_charges) > 0,
    hasDue: parseFloat(order.due_amount) > 0,
    is80,
  };

  const content = renderTemplate(CORPORATE_TEMPLATE, contentContext);
  const styles = buildCssVariables(ctx) + "\n" + CORPORATE_CSS;

  return renderTemplate(BASE_TEMPLATE, {
    title: `Invoice - ${order.order_no}`,
    styles,
    content,
  });
}
